/**
 * Physical sun from authored `environment.sun`, with sky brightness from `timeOfDay`.
 * World axes match the rest of the sim: +x is east, +y is south (screen-down), +z is up.
 * Compass bearings run 0 = north, clockwise.
 *
 * Water cast uses visual rise, not real metres. At zenith there is no side shade.
 */

import { add, clamp } from '../math.ts';
import { daylight } from '../environment/grade.ts';
import type { Vec2 } from '../types.ts';
import type { Environment, SunSettings } from './environment.ts';

export type Sun = {
  /** Unit vector pointing toward the sun. */
  direction: { x: number; y: number; z: number };
  /** Compass azimuth in radians (0 = north, clockwise). */
  azimuth: number;
  /** Elevation above the horizon in radians (0 = horizon, π/2 = zenith). */
  altitude: number;
  /** Direct sunlight: 0 at night and on the horizon, 1 at a clear noon. */
  irradiance: number;
  /** Authored strength after the horizon fade, not multiplied by altitude. */
  strength: number;
  /** Sky brightness for water and grading, following the 24 h daylight curve. */
  skyLight: number;
};

/** The sun is above the horizon between 06:00 and 18:00. */
const DAY_START = 0.25;
const DAY_END = 0.75;

/** Authored altitude (0–1) at and below which afternoon cast is full strength. */
export const CAST_FADE_START = 0.85;
/** Pixel cap so a low sun cannot smear across the map. */
export const CAST_OFFSET_CAP_PX = 280;

/**
 * The old coupled curve: one `timeOfDay` drove azimuth, altitude, and whether the sun is up.
 * Used to fill a missing `environment.sun` so a v4 file looks the same after load, and to
 * derive a disc when the author picks a Time preset or Randomize.
 */
export function sunSettingsFromTimeOfDay(timeOfDay: number): SunSettings {
  const tod = ((timeOfDay % 1) + 1) % 1;

  if (tod <= DAY_START || tod >= DAY_END) {
    return { azimuth: 0.5, altitude: 0, strength: 0 };
  }

  const t = (tod - DAY_START) / (DAY_END - DAY_START);
  const altitude = Math.asin(Math.min(1, Math.sin(Math.PI * t)));

  return {
    azimuth: t,
    altitude: altitude / (Math.PI / 2),
    strength: 1,
  };
}

export function evaluateSun(environment: Pick<Environment, 'timeOfDay' | 'sun'>): Sun {
  const tod = ((environment.timeOfDay % 1) + 1) % 1;
  const skyLight = daylight(tod);
  const altitude = environment.sun.altitude * (Math.PI / 2);
  const azimuth = ((90 + 180 * environment.sun.azimuth) * Math.PI) / 180;
  const cosAlt = Math.cos(altitude);
  const x = Math.sin(azimuth) * cosAlt;
  const y = -Math.cos(azimuth) * cosAlt;
  const z = Math.sin(altitude);
  const edgeFade = Math.min(1, 4 * environment.sun.altitude);

  return {
    direction: { x, y, z },
    azimuth,
    altitude,
    irradiance: environment.sun.strength * edgeFade * z,
    strength: environment.sun.strength * edgeFade,
    skyLight,
  };
}

/** Authored altitude 0–1. Full cast below the fade, zero at zenith. */
export function castFactor(altitude01: number): number {
  const altitude = clamp(altitude01, 0, 1);

  if (altitude >= 1) {
    return 0;
  }

  if (altitude <= CAST_FADE_START) {
    return 1;
  }

  return (1 - altitude) / (1 - CAST_FADE_START);
}

function authoredAltitude01(sun: Sun): number {
  return sun.altitude / (Math.PI / 2);
}

/**
 * World-pixel offset of a column's occupancy smear. Length follows the painted
 * cliff (`visualRisePx`), not real metres, so shade and extrusion stay in scale.
 * Zenith and night return zero — no footprint stamp.
 */
export function castOffsetPx(visualRise: number, sun: Sun): { x: number; y: number } {
  const factor = castFactor(authoredAltitude01(sun));

  if (visualRise <= 0 || sun.irradiance <= 0 || factor <= 0) {
    return { x: 0, y: 0 };
  }

  const ground = Math.hypot(sun.direction.x, sun.direction.y);

  if (ground < 1e-6 || sun.direction.z <= 1e-6) {
    return { x: 0, y: 0 };
  }

  const pixels = Math.min(CAST_OFFSET_CAP_PX, visualRise * (ground / sun.direction.z) * factor);

  return {
    x: -(sun.direction.x / ground) * pixels,
    y: -(sun.direction.y / ground) * pixels,
  };
}

function cross(origin: Vec2, a: Vec2, b: Vec2): number {
  return (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);
}

/** Monotone-chain hull; used to fill a plateau swept by a segment. */
function convexHull(points: readonly Vec2[]): Vec2[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);

  if (sorted.length <= 2) {
    return sorted.map((point) => ({ x: point.x, y: point.y }));
  }

  const lower: Vec2[] = [];

  for (const point of sorted) {
    while (lower.length >= 2) {
      const a = lower[lower.length - 2];
      const b = lower[lower.length - 1];

      if (!a || !b || cross(a, b, point) > 0) {
        break;
      }

      lower.pop();
    }

    lower.push({ x: point.x, y: point.y });
  }

  const upper: Vec2[] = [];

  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const point = sorted[i];

    if (!point) {
      continue;
    }

    while (upper.length >= 2) {
      const a = upper[upper.length - 2];
      const b = upper[upper.length - 1];

      if (!a || !b || cross(a, b, point) > 0) {
        break;
      }

      upper.pop();
    }

    upper.push({ x: point.x, y: point.y });
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Vertices of a column's occupancy: the convex hull of the footprint and that
 * footprint translated by `offset`. Overlapping hulls are one region when filled
 * at alpha 1. A zero offset is not a cast — callers skip the draw at zenith.
 */
export function sweptColumnHull(points: readonly Vec2[], offset: Vec2): Vec2[] {
  if (points.length < 3) {
    return [];
  }

  if (offset.x === 0 && offset.y === 0) {
    return points.map((point) => ({ x: point.x, y: point.y }));
  }

  return convexHull([...points, ...points.map((point) => add(point, offset))]);
}

/** Graphics shade opacity. Strength scales this; do not pass irradiance. */
export function shadeAlpha(base: number, strength: number): number {
  return clamp(base, 0, 1) * clamp(strength, 0, 1);
}
